import { format, parse } from 'date-fns';

/**
 * Calendar generator utility for Concal-GPT
 * Generates Google and Outlook calendar links with brand color #2f3b40
 */

const BRAND_COLOR = '#2f3b40';

/**
 * Generates Google Calendar link
 * @param {string} title - Event title
 * @param {string} date - Date in MM/DD/YYYY format
 * @param {string} time - Time (optional, defaults to all-day)
 * @param {string} description - Event description (optional)
 * @returns {string} Google Calendar URL
 */
export function generateGoogleCalendarLink(title, date, time = null, description = '') {
  try {
    const parsedDate = parse(date, 'MM/dd/yyyy', new Date());
    
    let startDate, endDate;
    if (time) {
      // Parse time and create datetime
      const [hours, minutes] = time.split(':').map(Number);
      parsedDate.setHours(hours, minutes, 0, 0);
      startDate = format(parsedDate, "yyyyMMdd'T'HHmmss");
      // Default 1-hour duration
      parsedDate.setHours(hours + 1, minutes, 0, 0);
      endDate = format(parsedDate, "yyyyMMdd'T'HHmmss");
    } else {
      // All-day event
      startDate = format(parsedDate, 'yyyyMMdd');
      endDate = format(parsedDate, 'yyyyMMdd');
    }
    
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: title,
      dates: `${startDate}/${endDate}`,
      details: description,
      ctz: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  } catch (error) {
    console.error('Google Calendar link generation error:', error);
    return '#';
  }
}

/**
 * Generates Outlook Calendar link
 * @param {string} title - Event title
 * @param {string} date - Date in MM/DD/YYYY format
 * @param {string} time - Time (optional, defaults to all-day)
 * @param {string} description - Event description (optional)
 * @returns {string} Outlook Calendar URL
 */
export function generateOutlookCalendarLink(title, date, time = null, description = '') {
  try {
    const parsedDate = parse(date, 'MM/dd/yyyy', new Date());
    
    let startDate, endDate;
    if (time) {
      const [hours, minutes] = time.split(':').map(Number);
      parsedDate.setHours(hours, minutes, 0, 0);
      startDate = parsedDate.toISOString();
      parsedDate.setHours(hours + 1, minutes, 0, 0);
      endDate = parsedDate.toISOString();
    } else {
      // All-day event
      startDate = format(parsedDate, 'yyyy-MM-dd');
      endDate = format(parsedDate, 'yyyy-MM-dd');
    }
    
    const params = new URLSearchParams({
      subject: title,
      startdt: startDate,
      enddt: endDate,
      body: description,
      allday: time ? 'false' : 'true'
    });
    
    return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
  } catch (error) {
    console.error('Outlook Calendar link generation error:', error);
    return '#';
  }
}

/**
 * Generates ICS calendar content for download
 * @param {Array} events - Array of event objects {title, date, time, description}
 * @param {string} propertyAddress - Property address for filename
 * @returns {object} ICS content and filename
 */
export function generateICSContent(events, propertyAddress) {
  try {
    const now = new Date();
    const timestamp = format(now, "yyyyMMdd'T'HHmmss'Z'");
    
    let icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Concal//Real Estate Contract Parser//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      `X-WR-CALNAME:${propertyAddress} - Contract Dates`,
      `X-WR-CALDESC:Important dates for ${propertyAddress} real estate transaction`,
      `X-APPLE-CALENDAR-COLOR:${BRAND_COLOR}`,
      ''
    ];
    
    events.forEach((event, index) => {
      const parsedDate = parse(event.date, 'MM/dd/yyyy', new Date());
      
      let dtstart, dtend;
      if (event.time) {
        const [hours, minutes] = event.time.split(':').map(Number);
        parsedDate.setHours(hours, minutes, 0, 0);
        dtstart = format(parsedDate, "yyyyMMdd'T'HHmmss");
        parsedDate.setHours(hours + 1, minutes, 0, 0);
        dtend = format(parsedDate, "yyyyMMdd'T'HHmmss");
      } else {
        dtstart = format(parsedDate, 'yyyyMMdd');
        dtend = format(parsedDate, 'yyyyMMdd');
      }
      
      icsContent.push(
        'BEGIN:VEVENT',
        `UID:${timestamp}-${index}@concal.app`,
        `DTSTAMP:${timestamp}`,
        `DTSTART${event.time ? '' : ';VALUE=DATE'}:${dtstart}`,
        `DTEND${event.time ? '' : ';VALUE=DATE'}:${dtend}`,
        `SUMMARY:${event.title}`,
        `DESCRIPTION:${event.description || ''}`,
        `CATEGORIES:Real Estate,Contract`,
        `COLOR:${BRAND_COLOR}`,
        'END:VEVENT',
        ''
      );
    });
    
    icsContent.push('END:VCALENDAR');
    
    const filename = `${propertyAddress.replace(/[^a-zA-Z0-9]/g, '_')}_contract_dates.ics`;
    
    return {
      content: icsContent.join('\r\n'),
      filename,
      mimeType: 'text/calendar'
    };
  } catch (error) {
    console.error('ICS generation error:', error);
    return null;
  }
}

/**
 * Creates calendar event object for Concal-GPT format
 * @param {string} propertyAddress - Property street address
 * @param {string} deadlineName - Name of the deadline
 * @param {string} date - Date in MM/DD/YYYY format
 * @param {string} time - Time (optional)
 * @returns {object} Calendar event with Google and Outlook links
 */
export function createCalendarEvent(propertyAddress, deadlineName, date, time = '17:00') {
  const eventTitle = `${propertyAddress} – ${deadlineName}`;
  
  return {
    title: eventTitle,
    date,
    time,
    googleLink: generateGoogleCalendarLink(eventTitle, date, time),
    outlookLink: generateOutlookCalendarLink(eventTitle, date, time),
    description: `Important deadline for ${propertyAddress} real estate transaction`
  };
}

export default {
  generateGoogleCalendarLink,
  generateOutlookCalendarLink,
  generateICSContent,
  createCalendarEvent
};